// Rolling frame statistics for the performance meter: frames per second actually drawn
// and milliseconds of work per frame, over the last second.

export interface FrameSample {
  readonly time: number;
  readonly workMs: number;
}

export class PerfMonitor {
  private readonly samples: FrameSample[] = [];
  private readonly windowMs: number;

  constructor(windowMs = 1000) {
    this.windowMs = windowMs;
  }

  record(time: number, workMs: number): void {
    this.samples.push({ time, workMs });
    this.trim(time);
  }

  /** Frames drawn during the last window ending at `now`; 0 when nothing was drawn. */
  fps(now: number): number {
    this.trim(now);
    return (this.samples.length * 1000) / this.windowMs;
  }

  /** Average work per drawn frame in the window, in milliseconds. */
  workMs(now: number): number {
    this.trim(now);
    if (this.samples.length === 0) return 0;
    return this.samples.reduce((s, f) => s + f.workMs, 0) / this.samples.length;
  }

  /** Slowest frame interval in the window (a stutter shows up here), in milliseconds. */
  worstIntervalMs(now: number): number {
    this.trim(now);
    let worst = 0;
    for (let i = 1; i < this.samples.length; i++) {
      const a = this.samples[i - 1] as FrameSample;
      const b = this.samples[i] as FrameSample;
      worst = Math.max(worst, b.time - a.time);
    }
    return worst;
  }

  private trim(now: number): void {
    while (this.samples.length > 0 && now - (this.samples[0] as FrameSample).time >= this.windowMs) this.samples.shift();
  }
}
